import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert, Button, Form } from "react-bootstrap";

import axiosRestaurantInstance from "../restaurant/AxiosRestaurantInstance";
import FloatingLabelInput from "../restaurant/PlaceHolder";
import styles from "../restaurant/css/SignIn.module.css";
import loginHero from "../restaurant/assets/maya-business-login-hero.png";
import MayaBrandMark from "../components/MayaBrandMark";
import { ORGANIZATION_API_PATHS } from "../constants/apiPaths";
import "./PartnerLogin.css";

export const PARTNER_FORGOT_PASSWORD_PATH = "/partner/forgot-password";

const PARTNER_LOGIN_PATH = "/partner/login";
const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;
const PASSWORD_POLICY_REGEX = /^[A-Z](?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{5,}$/;
const PASSWORD_POLICY_HINT =
  "At least 6 characters, starting with an uppercase letter, with lowercase letters, a digit and a special character.";

const apiError = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || (err?.response ? fallback : "Cannot reach the server. Please try again.");

const PartnerForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("request");
  const [channel, setChannel] = useState("email");
  const [identifier, setIdentifier] = useState("");
  const [destination, setDestination] = useState("");
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  useEffect(() => {
    if (step === "otp") otpRefs.current[0]?.focus();
  }, [step]);

  const switchChannel = (next) => {
    if (next === channel) return;
    setChannel(next);
    setIdentifier("");
    setError("");
  };

  const sendOtp = async () => {
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const { data } = await axiosRestaurantInstance.post(
        ORGANIZATION_API_PATHS.partnerForgotPassword,
        { channel, identifier: identifier.trim() },
        { skipAuthRefresh: true }
      );
      setDestination(data?.destination || identifier.trim());
      setOtp(Array(OTP_LENGTH).fill(""));
      setResendIn(RESEND_SECONDS);
      setStep("otp");
      setInfo(data?.message || "");
    } catch (err) {
      setError(apiError(err, "Could not send the code. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSubmit = (e) => {
    e.preventDefault();
    const value = identifier.trim();
    if (channel === "mobile" && value.replace(/\D/g, "").length !== 10) {
      setError("Enter a valid 10-digit mobile number.");
      return;
    }
    if (channel === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      setError("Enter a valid email address.");
      return;
    }
    sendOtp();
  };

  const setOtpDigits = (startIndex, digits) => {
    const next = [...otp];
    digits.split("").forEach((d, i) => {
      if (startIndex + i < OTP_LENGTH) next[startIndex + i] = d;
    });
    setOtp(next);
    const focusIndex = Math.min(startIndex + digits.length, OTP_LENGTH - 1);
    otpRefs.current[focusIndex]?.focus();
  };

  const handleOtpChange = (index, raw) => {
    const digits = raw.replace(/\D/g, "");
    if (!digits) {
      const next = [...otp];
      next[index] = "";
      setOtp(next);
      return;
    }
    setOtpDigits(index, digits.slice(0, OTP_LENGTH - index));
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!digits) return;
    e.preventDefault();
    setOtpDigits(0, digits);
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length !== OTP_LENGTH) {
      setError("Enter the 6-digit code.");
      return;
    }
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const { data } = await axiosRestaurantInstance.post(
        ORGANIZATION_API_PATHS.partnerVerifyResetOtp,
        { channel, identifier: identifier.trim(), otp: code },
        { skipAuthRefresh: true }
      );
      setResetToken(data?.resetToken || "");
      setStep("reset");
    } catch (err) {
      setError(apiError(err, "Verification failed. Please try again."));
      if (err?.response?.status === 429) setOtp(Array(OTP_LENGTH).fill(""));
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!PASSWORD_POLICY_REGEX.test(newPassword)) {
      setError(PASSWORD_POLICY_HINT);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axiosRestaurantInstance.post(
        ORGANIZATION_API_PATHS.partnerResetPassword,
        { resetToken, newPassword },
        { skipAuthRefresh: true }
      );
      setStep("done");
    } catch (err) {
      setError(apiError(err, "Could not reset the password. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep("request");
    setOtp(Array(OTP_LENGTH).fill(""));
    setResetToken("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setInfo("");
  };

  const subtitle = {
    request: "Choose where to receive a verification code.",
    otp: `Enter the 6-digit code sent to ${destination}.`,
    reset: "Create a new password for your account.",
    done: "Your password has been reset.",
  }[step];

  return (
    <div className={styles.signin_container}>
      <div className={styles.signin_leftSection}>
        <img src={loginHero} alt="Eats and grocery business operations" className={styles.signin_hero} />
        <div className={styles.signin_leftOverlay}>
          <p className={styles.signin_leftEyebrow}>Partner access for</p>
          <h1 className={styles.signin_leftTitle}>Business Operating System</h1>
          <p className={styles.signin_leftSubtitle}>
            One login for All Local Stores of Restaurants, Stores and Pharmacies.
          </p>
        </div>
      </div>

      <div className={styles.signin_rightSection}>
        <div className={styles.signin_form}>
          <div className="partner-login-brand">
            <MayaBrandMark variant="login" />
            <h1 className="partner-login-heading">Forgot Password</h1>
            <p className="partner-forgot-subtitle">{subtitle}</p>
          </div>

          {error ? (
            <Alert variant="danger" className="partner-forgot-alert">
              {error}
            </Alert>
          ) : null}
          {info && !error && step === "otp" ? (
            <Alert variant="info" className="partner-forgot-alert">
              {info}
            </Alert>
          ) : null}

          {step === "request" ? (
            <Form onSubmit={handleRequestSubmit} noValidate>
              <div className="partner-forgot-channels" role="radiogroup" aria-label="Send code to">
                {[
                  { id: "email", label: "Email" },
                  { id: "mobile", label: "Mobile" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    role="radio"
                    aria-checked={channel === opt.id}
                    className={`partner-forgot-channel${channel === opt.id ? " partner-forgot-channel--active" : ""}`}
                    onClick={() => switchChannel(opt.id)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <FloatingLabelInput
                type={channel === "email" ? "email" : "tel"}
                inputMode={channel === "email" ? "email" : "numeric"}
                maxLength={channel === "email" ? undefined : 10}
                value={identifier}
                onChange={(e) =>
                  setIdentifier(channel === "email" ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 10))
                }
                label={channel === "email" ? "Registered email" : "Registered mobile number"}
                isRequired
              />
              <br />
              <Button type="submit" disabled={loading} className={styles.button1}>
                {loading ? "Sending..." : "Send OTP"}
              </Button>
            </Form>
          ) : null}

          {step === "otp" ? (
            <Form onSubmit={handleOtpSubmit} noValidate>
              <div className="partner-forgot-otp" onPaste={handleOtpPaste}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => {
                      otpRefs.current[i] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    maxLength={OTP_LENGTH}
                    className="partner-forgot-otp__box"
                    value={digit}
                    aria-label={`Digit ${i + 1}`}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    onFocus={(e) => e.target.select()}
                  />
                ))}
              </div>
              <Button type="submit" disabled={loading} className={styles.button1}>
                {loading ? "Verifying..." : "Verify OTP"}
              </Button>
              <div className="partner-forgot-row">
                <button type="button" className="partner-forgot-link" onClick={restart} disabled={loading}>
                  Change {channel}
                </button>
                <button
                  type="button"
                  className="partner-forgot-link"
                  onClick={sendOtp}
                  disabled={loading || resendIn > 0}
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend OTP"}
                </button>
              </div>
            </Form>
          ) : null}

          {step === "reset" ? (
            <Form onSubmit={handleResetSubmit} noValidate>
              <FloatingLabelInput
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                label="New password"
                isRequired
              />
              <p className="partner-forgot-hint">{PASSWORD_POLICY_HINT}</p>
              <FloatingLabelInput
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                label="Confirm new password"
                isRequired
              />
              <br />
              <Button type="submit" disabled={loading} className={styles.button1}>
                {loading ? "Saving..." : "Reset Password"}
              </Button>
            </Form>
          ) : null}

          {step === "done" ? (
            <Button type="button" className={styles.button1} onClick={() => navigate(PARTNER_LOGIN_PATH)}>
              Back to Sign In
            </Button>
          ) : null}

          {step !== "done" ? (
            <p className="partner-forgot-footer">
              Remembered your password? <Link to={PARTNER_LOGIN_PATH}>Sign in</Link>
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PartnerForgotPassword;
